/**
 * ----------------------------------------------------------------------------------------------------------------------
 * FREE GIFT CART CLEANUP
 * ----------------------------------------------------------------------------------------------------------------------
 *
 * Gift lines added by the "Free gift picker" block carry a "_free_gift_for" property holding the ID of the product they
 * came with. Shopify has no notion of bundled lines, so if the shopper removes that product the gift would stay in the
 * cart on its own. This script removes those orphaned gift lines.
 *
 * It reacts to two situations:
 *
 *   - "cart:change", fired by the theme whenever the cart is mutated without a page load (the cart drawer). The event
 *     already carries the full cart, so no extra request is needed.
 *   - orphans detected server side while rendering the page, handed over in window.freeGiftOrphanKeys. This covers full
 *     page loads, including the reload the theme performs after a removal on the cart page.
 */

(() => {
  const PARENT_PROPERTY = '_free_gift_for';

  let isCleaning = false;

  /** Gift lines whose parent product is no longer anywhere in the cart. */
  const findOrphanKeys = (items) => {
    const parentProductIds = new Set(
      items.filter((item) => !item.properties?.[PARENT_PROPERTY]).map((item) => String(item.product_id))
    );

    return items
      .filter((item) => {
        const parentId = item.properties?.[PARENT_PROPERTY];
        return parentId && !parentProductIds.has(String(parentId));
      })
      .map((item) => item.key);
  };

  const removeLines = async (keys) => {
    if (isCleaning || keys.length === 0) {
      return;
    }

    isCleaning = true;
    let removedCount = 0;

    try {
      for (const key of keys) {
        const response = await fetch(`${Shopify.routes.root}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ id: key, quantity: 0 })
        });

        if (response.ok) {
          removedCount += 1;
        }
      }
    } finally {
      isCleaning = false;
    }

    // Only refresh when something actually changed, so a removal that keeps failing cannot turn into a reload loop.
    if (removedCount === 0) {
      return;
    }

    if (window.themeVariables?.settings?.pageType === 'cart') {
      window.location.reload();
    } else {
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    }
  };

  document.addEventListener('cart:change', (event) => {
    const items = event.detail?.cart?.items;

    if (Array.isArray(items)) {
      removeLines(findOrphanKeys(items));
    }
  });

  if (Array.isArray(window.freeGiftOrphanKeys)) {
    removeLines(window.freeGiftOrphanKeys);
  }
})();
